import { Link, router, usePage } from "@inertiajs/react"
import { AlertTriangle, BellRing, Check } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { markAsReadNotificationPath, notificationsPath } from "@/routes"

interface Notification {
  id: number
  type: string
  subject: string | null
  message: string
  body: string | null
  url: string | null
  urgent: boolean
  sender_name: string | null
  read_at: string | null
  created_at: string
}

export function NotificationDropdown() {
  const { notifications: sharedNotifications } = usePage().props as {
    notifications: { unread_count: number }
  }
  const unreadCount = sharedNotifications.unread_count

  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(notificationsPath({ format: "json" }))
      const data = (await res.json()) as {
        notifications: Notification[]
        has_more: boolean
      }
      setNotifications(data.notifications)
      setHasMore(data.has_more)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      void fetchNotifications()
    }
  }, [open, fetchNotifications])

  const handleMarkAsRead = (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    e.preventDefault()

    void fetch(markAsReadNotificationPath({ id }, { format: "json" }), {
      method: "PATCH",
      headers: {
        "X-CSRF-Token":
          document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
            ?.content ?? "",
      },
    }).then(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id))
      router.reload({ only: ["notifications"] })
    })
  }

  const handleNotificationClick = (notification: Notification) => {
    setOpen(false)
    if (notification.url) {
      router.visit(notification.url)
    }
  }

  const timeAgo = (dateString: string) => {
    const seconds = Math.floor(
      (Date.now() - new Date(dateString).getTime()) / 1000,
    )
    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  const hasUnread = unreadCount > 0

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative flex cursor-pointer items-center gap-1.5 text-white/80 hover:text-white"
        title="Notifications"
      >
        <BellRing className={`h-5 w-5 ${hasUnread ? "text-hpi-yellow" : ""}`} />
        {hasUnread && (
          <span className="bg-hpi-yellow flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-black">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="border-hpi-light-gray absolute top-full right-0 z-50 mt-2 w-96 rounded-lg border bg-white shadow-lg">
            {/* Header */}
            <div className="border-hpi-pale-gray flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-hpi-dark-gray text-sm font-semibold">
                Notifications
              </h3>
              {hasUnread && (
                <span className="bg-hpi-yellow/20 text-hpi-dark-gray rounded-full px-2 py-0.5 text-xs font-medium">
                  {unreadCount} unread
                </span>
              )}
            </div>

            {/* Notification list */}
            <div className="max-h-80 overflow-y-auto">
              {loading && notifications.length === 0 && (
                <div className="text-hpi-med-gray px-4 py-8 text-center text-sm">
                  Loading...
                </div>
              )}

              {!loading && notifications.length === 0 && (
                <div className="text-hpi-med-gray px-4 py-8 text-center text-sm">
                  No unread notifications
                </div>
              )}

              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="group border-hpi-pale-gray hover:bg-hpi-pale-gray flex items-start gap-3 border-b px-4 py-3 last:border-b-0"
                >
                  {notification.urgent && (
                    <AlertTriangle className="text-hpi-red mt-1 h-4 w-4 shrink-0" />
                  )}
                  <div
                    className={`mt-1 flex-1 ${notification.url ? "cursor-pointer" : ""}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {notification.subject && (
                      <p
                        className={`text-sm font-medium ${notification.urgent ? "text-hpi-red" : "text-hpi-dark-gray"}`}
                      >
                        {notification.subject}
                      </p>
                    )}
                    <p className="text-hpi-dark-gray text-sm">
                      {notification.message}
                    </p>
                    <p className="text-hpi-med-gray mt-0.5 text-xs">
                      {notification.sender_name && (
                        <span>{notification.sender_name} &middot; </span>
                      )}
                      {timeAgo(notification.created_at)}
                    </p>
                  </div>

                  {!notification.read_at && (
                    <button
                      onClick={(e) => handleMarkAsRead(e, notification.id)}
                      className="text-hpi-med-gray hover:bg-hpi-light-gray hover:text-hpi-dark-green mt-1 shrink-0 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100"
                      title="Mark as read"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-hpi-pale-gray border-t px-4 py-2">
              <Link
                href={notificationsPath()}
                className="text-hpi-active-blue block text-center text-sm font-medium hover:underline"
                onClick={() => setOpen(false)}
              >
                {hasMore
                  ? `View all notifications (${unreadCount - 20} more)`
                  : "View all notifications"}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
